import { CalendarPlus, MessageSquare } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { GestorJornadaRegisterForm } from '@/components/Gestores/GestorJornadaRegisterForm'
import { GestorTableAvatar } from '@/components/Gestores/GestorListTableShell'
import {
  jornadaCadenciaLabel,
  jornadaPrazoClass,
  jornadaPrazoLabel,
  jornadaProximaLabel,
  jornadaRowToneClass,
  jornadaSetorLabel,
  jornadaStatusBadge,
  jornadaUltimaLabel,
} from '@/components/Gestores/gestorJornadaMembroUi'
import type { JornadaCadenciaDias } from '@/components/Gestores/gestorJornadaUi'
import type { GestorJornadaMembro } from '@/services/gestoresService'
import { cn } from '@/utils/cn'

type Props = {
  membro: GestorJornadaMembro
  expanded: boolean
  registrando: boolean
  onToggleRegister: () => void
  onOpenFeedback: () => void
  onCadenciaChange: (cadencia: JornadaCadenciaDias) => Promise<void>
  onRegister: (body: { realizadaEm: string; notas?: string }) => Promise<void>
}

export function GestorJornadaMembroCard({
  membro,
  expanded,
  registrando,
  onToggleRegister,
  onOpenFeedback,
  onCadenciaChange,
  onRegister,
}: Props) {
  const status = jornadaStatusBadge(membro.situacao)

  return (
    <li
      className={cn(
        'overflow-hidden rounded-xl border border-border/80 bg-white/[0.02]',
        jornadaRowToneClass(membro.situacao),
      )}
    >
      <div className='grid gap-3 p-3 md:grid-cols-12 md:items-center md:gap-4 md:p-4'>
        <div className='flex min-w-0 items-center gap-3 md:col-span-4'>
          <GestorTableAvatar name={membro.nomeCompleto} />
          <div className='min-w-0'>
            <p className='truncate text-sm font-medium text-text-primary'>{membro.nomeCompleto}</p>
            <p className='truncate text-xs text-text-muted'>{jornadaSetorLabel(membro.setor)}</p>
          </div>
        </div>

        <div className='grid grid-cols-2 gap-2 text-xs md:col-span-4 md:grid-cols-1'>
          <p className='text-text-muted'>
            Última 1:1{' '}
            <span className='font-medium text-text-primary'>
              {membro.ultimaOneOnOneEm ? jornadaUltimaLabel(membro.ultimaOneOnOneEm) : 'Nunca registrada'}
            </span>
          </p>
          <p className='text-text-muted'>
            Próxima{' '}
            <span className='font-medium text-text-primary'>
              {jornadaProximaLabel(membro.proximaOneOnOneEm)}
            </span>
            <span className={cn('ml-1', jornadaPrazoClass(membro.proximaOneOnOneEm))}>
              ({jornadaPrazoLabel(membro.proximaOneOnOneEm)})
            </span>
          </p>
          <p className='col-span-2 text-text-muted md:col-span-1'>
            Ciclo{' '}
            <span className='font-medium text-text-primary'>
              {jornadaCadenciaLabel(membro.cadenciaDias)}
            </span>
          </p>
        </div>

        <div className='flex flex-wrap items-center justify-between gap-2 md:col-span-4 md:justify-end'>
          <span
            className={cn(
              'rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide ring-1 ring-inset',
              status.className,
            )}
          >
            {status.label}
          </span>
          <div className='flex items-center gap-1.5'>
            <Button
              type='button'
              size='sm'
              variant={expanded ? 'primary' : 'secondary'}
              onClick={onToggleRegister}
            >
              <CalendarPlus size={14} />
              Registrar 1:1
            </Button>
            <Button type='button' size='sm' variant='ghost' onClick={onOpenFeedback} title='Feedback'>
              <MessageSquare size={14} />
            </Button>
          </div>
        </div>
      </div>

      {expanded && (
        <GestorJornadaRegisterForm
          loading={registrando}
          cadenciaDias={membro.cadenciaDias}
          onCadenciaChange={(c) => void onCadenciaChange(c)}
          onCancel={onToggleRegister}
          onSubmit={async (body) => {
            try {
              await onRegister(body)
              onToggleRegister()
            } catch {
              // mantém aberto
            }
          }}
        />
      )}
    </li>
  )
}
